import { useState, useCallback } from "react";
import { addStore, getStoreFromInformera } from "../services/stores.service";
import { Store, AddStoreData } from "../types/Stores.types";

type StoreType = "REGULAR" | "PAOP";

interface UseStoreSearchReturn {
  searchId: string;
  setSearchId: (id: string) => void;
  foundStore: Store | null;
  foundIn: StoreType | null;
  selectedPrimaryLocale: string;
  setSelectedPrimaryLocale: (locale: string) => void;
  selectedCountryId: string;
  setSelectedCountryId: (countryId: string) => void;
  storeType: StoreType;
  setStoreType: (type: StoreType) => void;
  isSearching: boolean;
  isImporting: boolean;
  errorMessage: string;
  successMessage: string;
  searchStore: () => Promise<void>;
  importFoundStore: () => Promise<void>;
  clearSuccess: () => void;
}

export const useStoreSearch = (): UseStoreSearchReturn => {
  const [searchId, setSearchId] = useState("");
  const [foundStore, setFoundStore] = useState<Store | null>(null);
  const [foundIn, setFoundIn] = useState<StoreType | null>(null);
  const [selectedPrimaryLocale, setSelectedPrimaryLocale] = useState("");
  const [selectedCountryId, setSelectedCountryId] = useState("SE");
  const [storeType, setStoreType] = useState<StoreType>("REGULAR");
  const [isSearching, setIsSearching] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const clearSuccess = useCallback(() => {
    setSuccessMessage("");
  }, []);

  const searchStore = useCallback(async () => {
    if (!searchId.trim()) {
      setErrorMessage("Please enter a store ID");
      return;
    }

    setIsSearching(true);
    setErrorMessage("");
    setFoundStore(null);
    setFoundIn(null);
    setSelectedPrimaryLocale("");

    try {
      const result = await getStoreFromInformera(searchId.trim(), storeType);

      if (result) {
        setFoundStore(result);
        setFoundIn(storeType);
        // Auto-select first locale as default
        const firstLocale = result.locales[0];
        setSelectedPrimaryLocale(firstLocale);
      } else {
        setErrorMessage(`Store with ID "${searchId}" not found in ${storeType} stores`);
      }
    } catch (error) {
      setErrorMessage(`Error searching for store: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsSearching(false);
    }
  }, [searchId, storeType]);

  const importFoundStore = useCallback(async () => {
    if (!foundStore || !selectedPrimaryLocale || !foundIn || !selectedCountryId) {
      setErrorMessage("Please select both a primary language and target country before importing");
      return;
    }

    setIsImporting(true);
    setErrorMessage("");

    try {
      const storeData: AddStoreData = {
        storeId: foundStore.storeId,
        isVirtual: foundStore.isVirtual,
        countryId: selectedCountryId,
        name: foundStore.name,
        status: foundStore.status,
        locales: foundStore.locales,
        primaryLocale: selectedPrimaryLocale,
        timezone: foundStore.timezone,
        createdAt: Date.now(),
      };

      await addStore(storeData);

      const storeTypeLabel = foundIn === "REGULAR" ? "Regular Store" : "PAOP Store (Blue Box/Lada/PAOP)";
      setSuccessMessage(
        `${storeTypeLabel} "${foundStore.name}" imported successfully to ${selectedCountryId} with ${selectedPrimaryLocale} as primary language`
      );

      // Reset form after successful import
      setSearchId("");
      setFoundStore(null);
      setFoundIn(null);
      setSelectedPrimaryLocale("");
      setSelectedCountryId("SE");
    } catch (error) {
      setErrorMessage(`Error importing store: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsImporting(false);
    }
  }, [foundStore, selectedPrimaryLocale, foundIn, selectedCountryId]);

  return {
    searchId,
    setSearchId,
    foundStore,
    foundIn,
    selectedPrimaryLocale,
    setSelectedPrimaryLocale,
    selectedCountryId,
    setSelectedCountryId,
    storeType,
    setStoreType,
    isSearching,
    isImporting,
    errorMessage,
    successMessage,
    searchStore,
    importFoundStore,
    clearSuccess,
  };
};
